import { useEffect, useRef, useState } from "react";
import { api, type BragOffer } from "./api";
import { Lion } from "./Lion";
import "./BragToParents.css";

// Brag to your parents (decision 83): after a successful Submit, a pop-up offers to email the
// student's parents a private link to what they made. It only opens when the server says so
// (the switch is on, email is set up, the teacher added a parent email, not sent yet, under the
// daily limit). The student never sees the addresses: the server looks them up when it sends.

export const MAX_NOTE = 200;

export function BragToParents({ projectId, submissionId, offer, onClose }: { projectId: string; submissionId: number; offer: BragOffer; onClose: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);
  const noteBox = useRef<HTMLTextAreaElement>(null);
  const p = offer.preview!;
  const both = offer.parents > 1;

  useEffect(() => {
    noteBox.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.sendBrag(projectId, submissionId, note.trim());
      setSent(r.sent);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const trimmed = note.replace(/\s+/g, " ").trim();
  return (
    <div className="dlg-backdrop brag-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg brag" role="dialog" aria-modal="true" aria-labelledby="brag-title">
        {sent !== null ? (
          <div className="dlg-done brag-sent">
            <Lion pose="cheer" size={96} label="Roary the lion, cheering" />
            <h2 id="brag-title">Sent! 🎉</h2>
            <p>{sent > 1 ? "Your parents will each get an email" : "Your parent will get an email"} with a link to your project.</p>
            <p className="muted small">The link shows your project as you turned it in. Keep building!</p>
            <button type="button" className="primary" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="brag-head">
              <Lion pose="wave" size={72} label="Roary the lion, waving" />
              <div>
                <span className="eyebrow">Nice work!</span>
                <h2 id="brag-title">Brag to your parents?</h2>
                <p className="muted small">
                  Send {both ? "both parents' emails on file" : "your parent's email on file"} a private link to what you made. They'll see your project and your note, not your exit ticket.
                </p>
              </div>
            </div>

            <label className="reflect brag-note">
              <span>Add a short note (optional)</span>
              <textarea ref={noteBox} rows={2} maxLength={MAX_NOTE} value={note} placeholder="I made the buttons change color!" onChange={(e) => setNote(e.target.value)} />
              <span className="muted small brag-count" aria-live="polite">
                {note.length}/{MAX_NOTE}
              </span>
            </label>

            <section className="brag-preview" aria-label="What the email will say">
              <span className="eyebrow">The email</span>
              <p className="brag-subject">
                <b>Subject:</b> {p.subject}
              </p>
              <div className="brag-mail">
                <p>
                  {p.first} just turned in <b>{p.lesson}</b> in BuildLab and wanted you to see it.
                </p>
                {trimmed && (
                  <p className="brag-mail-note">
                    <b>A note from {p.first}:</b> {trimmed}
                  </p>
                )}
                <span className="brag-mail-button">See what {p.first} made</span>
                <p className="muted small">
                  You're getting this because {p.teacher} added your email to BuildLab for your student.
                </p>
              </div>
            </section>

            {error && <p className="dlg-error">{error}</p>}
            <div className="dlg-foot">
              <button type="button" className="linklike" onClick={onClose}>
                Not now
              </button>
              <button type="button" className="primary" disabled={busy} onClick={() => void send()}>
                {busy ? "Sending…" : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
